/**
 * Safe Custody — directApiClient (LF-3216)
 *
 * Server actions retired in favour of `directApiClient`. Cache invalidation
 * happens via React Query at the hook layer (see hooks.tsx +
 * query-dependencies.ts `safe-custody`).
 *
 * Function names + ActionResult shape preserved so existing callers
 * (hooks + any non-hook consumers) keep working without signature churn.
 */

'use client';

import { directApiClient, DirectApiError } from '@/lib/api/direct-client';
import type {
  SafeCustodyPacketDetail,
  SafeCustodyActionResult,
  CreateSafeCustodyPacketRequest,
  UpdateSafeCustodyPacketRequest
} from './types';

function toErrorResult<T>(error: unknown): SafeCustodyActionResult<T> {
  if (error instanceof DirectApiError) {
    return { success: false, error: error.message };
  }
  return {
    success: false,
    error: error instanceof Error ? error.message : 'Request failed'
  };
}

export async function createSafeCustodyPacketAction(
  request: CreateSafeCustodyPacketRequest
): Promise<SafeCustodyActionResult<SafeCustodyPacketDetail>> {
  try {
    const packet = await directApiClient.post<SafeCustodyPacketDetail>(
      '/safe-custody/packets',
      request
    );
    return { success: true, data: packet };
  } catch (error) {
    return toErrorResult<SafeCustodyPacketDetail>(error);
  }
}

export async function updateSafeCustodyPacketAction(
  packetId: number,
  request: UpdateSafeCustodyPacketRequest
): Promise<SafeCustodyActionResult<SafeCustodyPacketDetail>> {
  try {
    const packet = await directApiClient.patch<SafeCustodyPacketDetail>(
      `/safe-custody/packets/${packetId}`,
      request
    );
    return { success: true, data: packet };
  } catch (error) {
    return toErrorResult<SafeCustodyPacketDetail>(error);
  }
}

export async function deleteSafeCustodyPacketAction(
  packetId: number
): Promise<SafeCustodyActionResult<void>> {
  try {
    await directApiClient.delete(`/safe-custody/packets/${packetId}`);
    return { success: true, data: undefined };
  } catch (error) {
    return toErrorResult<void>(error);
  }
}

export async function addDocumentAction(
  packetId: number,
  name: string
): Promise<SafeCustodyActionResult<SafeCustodyPacketDetail>> {
  try {
    const packet = await directApiClient.post<SafeCustodyPacketDetail>(
      `/safe-custody/packets/${packetId}/documents`,
      { name }
    );
    return { success: true, data: packet };
  } catch (error) {
    return toErrorResult<SafeCustodyPacketDetail>(error);
  }
}

export async function removeDocumentAction(
  packetId: number,
  documentId: number,
  reason?: string
): Promise<SafeCustodyActionResult<SafeCustodyPacketDetail>> {
  try {
    const packet = await directApiClient.post<SafeCustodyPacketDetail>(
      `/safe-custody/packets/${packetId}/documents/${documentId}/remove`,
      { reason }
    );
    return { success: true, data: packet };
  } catch (error) {
    return toErrorResult<SafeCustodyPacketDetail>(error);
  }
}

export async function deleteDocumentAction(
  packetId: number,
  documentId: number,
  reason?: string
): Promise<SafeCustodyActionResult<SafeCustodyPacketDetail>> {
  try {
    const packet = await directApiClient.post<SafeCustodyPacketDetail>(
      `/safe-custody/packets/${packetId}/documents/${documentId}/delete`,
      { reason }
    );
    return { success: true, data: packet };
  } catch (error) {
    return toErrorResult<SafeCustodyPacketDetail>(error);
  }
}

export async function restoreDocumentAction(
  packetId: number,
  documentId: number
): Promise<SafeCustodyActionResult<SafeCustodyPacketDetail>> {
  try {
    const packet = await directApiClient.post<SafeCustodyPacketDetail>(
      `/safe-custody/packets/${packetId}/documents/${documentId}/restore`,
      {}
    );
    return { success: true, data: packet };
  } catch (error) {
    return toErrorResult<SafeCustodyPacketDetail>(error);
  }
}
